import { Signal } from "signals";
import SymbolBehaviourDriver from "slotworks/components/matrix/symbol/behaviours/symbol-behaviour-driver";
import { SymbolBehaviourMethod } from "slotworks/components/matrix/symbol/behaviours/symbol-behaviour-driver";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import GraphicsManager from "slotworks/graphics/graphics-manager";
import Layers from "slotworks/graphics/layers/layers";
import SlotDefinition from "slotworks/model/slot-definition";
import { SoundEvent } from "slotworks/sound/sound-events";
import SoundManager from "slotworks/sound/sound-manager";
import Contract from "slotworks/utils/contract";
import Point from "slotworks/utils/geom/point";
import Rectangle from "slotworks/utils/geom/rectangle";

export default class SymbolComponent extends SymbolBehaviourDriver {

    /**
     * Position in matrix where {x:1, y:1} if top left
     */
    public readonly gridPosition: Point;
    public readonly landSignal: Signal;

    /**
     * The target transform of a "normal" symbol.
     * Note this is always in terms of the MatrixContent layer
     */
    private transform: Rectangle;
    private visible: boolean = true;

    // TODO: Perhaps promote this to somewhere globally accessible
    private masterPosition: string = "symbol_1_1";

    private staticSprite: PIXI.Sprite;
    private staticBackground: PIXI.Sprite;
    private animatedBackground: AnimatedSprite;
    private animatedForeground: AnimatedSprite;

    private stackOffset: number = 0;
    private isStackPart: boolean;

    private waitingToStick: boolean;
    private moving: boolean;

    private staticSprites: Map<string, PIXI.Sprite>;
    private staticBackgrounds: Map<string, PIXI.Sprite>;
    private animatedBackgrounds: Map<string, AnimatedSprite>;
    private animatedForegrounds: Map<string, AnimatedSprite>;
    private staticSpriteOffsets: Map<string, Rectangle>;
    private staticBackgroundOffsets: Map<string, Rectangle>;
    private animatedBackgroundOffsets: Map<string, Rectangle>;
    private animatedForegroundOffsets: Map<string, Rectangle>;

    private layerLocked: boolean;
    private layer: Layers;

    constructor(gridPosition: Point) {
        super();

        this.landSignal = new Signal();

        this.gridPosition = gridPosition.clone();

        this.generateStaticSprites();

        this.setVisible(true);
    }

    public destroy() {
        this.staticSprites.forEach((sprite) => {
            sprite.parent.removeChild(sprite);
        });
        this.staticSprites.clear();

        this.animatedBackgrounds.forEach((sprite) => {
            sprite.parent.removeChild(sprite);
        });
        this.animatedBackgrounds.clear();

        this.animatedForegrounds.forEach((sprite) => {
            sprite.parent.removeChild(sprite);
        });
        this.animatedForegrounds.clear();

        this.staticBackgrounds.forEach((sprite) => {
            sprite.parent.removeChild(sprite);
        });
        this.staticBackgrounds.clear();
    }

    public static() {
        if (this.waitingToStick) {
            this.stick().execute();
        } else {
            this.updateDepth();

            this.executeBehaviour(SymbolBehaviourMethod.Static).execute();
        }
    }

    public land(): Contract<void> {
        SoundManager.event(SoundEvent.symbol_ID_land, this.symbolId);

        const symbolDef = SlotDefinition.getSymbolDefinition(this.symbolId);
        if (symbolDef.sticky) {

            if (this.visible && !this.proxy) {
                SoundManager.event(SoundEvent.stick_symbol);
                SoundManager.event(SoundEvent.stick_symbol_ID, this.symbolId);
            }

            this.waitingToStick = true;
        }

        this.landSignal.dispatch();

        return new Contract<void>((resolve) => {
            this.executeBehaviour(SymbolBehaviourMethod.Land).then(() => {
                if (this.waitingToStick) {
                    this.stick().execute();
                }
                resolve(null);
            });
        });
    }

    public setMoving(moving: boolean) {
        this.moving = moving;
    }

    public isMoving() {
        return this.moving;
    }

    public stick() {
        this.waitingToStick = false;
        return this.executeBehaviour(SymbolBehaviourMethod.Stick);
    }

    public expand(x: number = 0, y: number = 0): Contract<void> {
        SoundManager.event(SoundEvent.symbol_any_expand);
        SoundManager.event(SoundEvent.symbol_ID_expand, this.symbolId);
        if (y < 0) {
            SoundManager.event(SoundEvent.symbol_any_expand_up);
            SoundManager.event(SoundEvent.symbol_ID_expand_up, this.symbolId);
        } else if (y > 0) {
            SoundManager.event(SoundEvent.symbol_any_expand_down);
            SoundManager.event(SoundEvent.symbol_ID_expand_down, this.symbolId);
        }

        return this.executeBehaviour(SymbolBehaviourMethod.Expand, x, y);
    }

    public stop(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Stop);
    }

    public highlight(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Highlight);
    }

    public dim(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Dim);
    }

    public win(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Win);
    }

    public cycle(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Cycle);
    }

    public anticipate(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Anticipate);
    }

    public display(): Contract<void> {
        return this.executeBehaviour(SymbolBehaviourMethod.Display);
    }

    public setSymbol(id: string) {
        this.symbolId = id;

        this.executeBehaviour(SymbolBehaviourMethod.SymbolChange).execute();
    }

    public refresh() {
        this.setSymbol(this.symbolId);
    }

    /**
     * Sets the position and size of the static symbol
     * Anchor point is always centered
     *
     * @param transform {Rectangle}
     */
    public setTransform(transform: Rectangle) {
        this.transform = transform;
    }

    public offsetStack(offset: number) {
        this.stackOffset = offset;
    }

    public setIsStackPart(stackPart: boolean) {
        this.isStackPart = stackPart;
        this.updateVisible();
    }

    public isPartOfStack() {
        return this.isStackPart;
    }

    /**
     * Sets visibility for the static symbol
     * @param visible {boolean}
     */
    public setVisible(visible: boolean) {
        this.visible = visible;
        this.updateVisible();
    }
    public setSymbolFade(visible: boolean, fade: number = 0.3) {
        if (!visible) {
            this.staticSprite.alpha = fade;
        } else {
            this.staticSprite.alpha = 1;
        }
        this.updateVisible();
    }
    public tint(color: number) {
        const tintElements = [this.staticSprite, this.staticBackground, this.animatedBackground, this.animatedForeground];

        for (const element of tintElements) {
            if (element) {
                element.tint = color;
            }
        }
    }

    public setScale(scale: Point) {
        this.staticSprite.scale.x = scale.x;
        this.staticSprite.scale.y = scale.y;
    }

    public setMask(mask: PIXI.Sprite | PIXI.Graphics) {
        this.staticSprites.forEach((sprite) => {
            sprite.mask = mask;
        });
    }

    public setLayer(layer: Layers) {
        if (!this.layerLocked) {
            this.layer = layer;
            this.animatedBackgrounds.forEach((sprite) => {
                this.layer.add(sprite);
            });
            this.staticBackgrounds.forEach((sprite) => {
                this.layer.add(sprite);
            });
            this.staticSprites.forEach((sprite) => {
                this.layer.add(sprite);
            });
            this.animatedForegrounds.forEach((sprite) => {
                this.layer.add(sprite);
            });
        }
    }

    public lockLayer() {
        this.layerLocked = true;
    }

    public unlockLayer() {
        this.layerLocked = false;
    }

    public getLayer() {
        return this.layer;
    }

    public updateTransform() {

        // Translate transform to local co-ordinate space
        let transform = this.transform;

        if (this.layer !== Layers.MatrixContent) {
            const globalPosition = Layers.MatrixContent.localToGlobal(transform);
            transform = this.layer.globalToLocal(globalPosition);
        }

        this.updateStatic(transform);
        this.updateStaticBackground(transform);
        this.updateAnimatedBackground(transform);
        this.updateAnimatedForeground(transform);

        // Offset stacks
        if (this.stackOffset !== 0) {
            this.staticSprite.y -= this.getStackOffsetY();
        }

        this.updateVisible();
        this.updateDepth();

        this.executeBehaviour(SymbolBehaviourMethod.UpdateTransform).execute();
    }

    public getTransform(): Rectangle {
        return this.transform;
    }

    /**
     * Returns a perctange of the stack this belongs to. For example if
     * this is the middle symbol of a 3-high stack, this will return 0.3.
     * If it's the bottom symbol of a 3-high stack, this will return 0.6
     */
    public getStackOffsetY(): number {
        const symbolDef = SlotDefinition.getSymbolDefinition(this.symbolId);
        return (1 / symbolDef.size) * this.stackOffset * this.staticSprite.height;
    }

    public getStackOffset(): number {
        return this.stackOffset;
    }

    private updateStatic(transform: Rectangle) {
        // Set correct symbol
        this.staticSprite.visible = false;
        this.staticSprite = this.staticSprites.get(this.symbolId);
        this.staticSprite.visible = true;

        const offset = this.staticSpriteOffsets.get(this.symbolId);

        // Update basic transform
        this.staticSprite.width = transform.width * offset.width;
        this.staticSprite.height = transform.height * offset.height;

        this.staticSprite.x = offset.x + transform.x + this.staticSprite.width * 0.5;
        this.staticSprite.y = offset.y + transform.y + this.staticSprite.height * 0.5;
    }

    private updateStaticBackground(transform: Rectangle) {
        if (this.staticBackground) {
            this.staticBackground.visible = false;
            this.staticBackground = null;
        }
        if (this.staticBackgrounds.has(this.symbolId)) {
            this.staticBackground = this.staticBackgrounds.get(this.symbolId);

            this.staticBackground.visible = !this.proxy && !this.isStackPart;

            const bgOffset = this.staticBackgroundOffsets.get(this.symbolId);
            this.staticBackground.width = transform.width * bgOffset.width;
            this.staticBackground.height = transform.height * bgOffset.height;
            this.staticBackground.x = bgOffset.x + transform.x + this.staticBackground.width * 0.5;
            this.staticBackground.y = bgOffset.y + transform.y + this.staticBackground.height * 0.5;

        }
    }

    private updateAnimatedBackground(transform: Rectangle) {
        if (this.animatedBackground) {
            this.animatedBackground.stop();
            this.animatedBackground.visible = false;
            this.animatedBackground = null;
        }
        if (this.animatedBackgrounds.has(this.symbolId)) {
            this.animatedBackground = this.animatedBackgrounds.get(this.symbolId);

            this.animatedBackground.visible = !this.proxy && !this.isStackPart;
            this.animatedBackground.play();

            const bgOffset = this.animatedBackgroundOffsets.get(this.symbolId);
            this.animatedBackground.width = transform.width * bgOffset.width;
            this.animatedBackground.height = transform.height * bgOffset.height;
            this.animatedBackground.x = bgOffset.x + transform.x + this.animatedBackground.width * 0.5;
            this.animatedBackground.y = bgOffset.y + transform.y + this.animatedBackground.height * 0.5;

        }
    }

    private updateAnimatedForeground(transform: Rectangle) {
        if (this.animatedForeground) {
            this.animatedForeground.stop();
            this.animatedForeground.visible = false;
            this.animatedForeground = null;
        }
        if (this.animatedForegrounds.has(this.symbolId)) {
            this.animatedForeground = this.animatedForegrounds.get(this.symbolId);

            this.animatedForeground.visible = !this.proxy && !this.isStackPart;
            this.animatedForeground.play();

            const fgOffset = this.animatedForegroundOffsets.get(this.symbolId);
            this.animatedForeground.width = transform.width * fgOffset.width;
            this.animatedForeground.height = transform.height * fgOffset.height;
            this.animatedForeground.x = fgOffset.x + transform.x + this.animatedForeground.width * 0.5;
            this.animatedForeground.y = fgOffset.y + transform.y + this.animatedForeground.height * 0.5;

        }
    }

    private updateVisible() {
        this.staticSprite.visible = this.visible && !this.isStackPart;
    }

    private updateDepth() {
        if (this.layer === Layers.MatrixContent) {
            const symbolDef = SlotDefinition.getSymbolDefinition(this.symbolId);

            if (symbolDef && symbolDef.alwaysOnTop) {
                if (this.layer === Layers.MatrixContent) {
                    this.layer.add(this.staticSprite);
                }
            }
        }
    }

    private generateStaticSprites() {
        this.layer = Layers.MatrixContent;

        const masterRect = Layers.MatrixContent.getPosition(this.masterPosition);

        this.staticSprites = new Map<string, PIXI.Sprite>();
        this.staticBackgrounds = new Map<string, PIXI.Sprite>();
        this.animatedBackgrounds = new Map<string, AnimatedSprite>();
        this.animatedForegrounds = new Map<string, AnimatedSprite>();
        this.staticSpriteOffsets = new Map<string, Rectangle>();
        this.staticBackgroundOffsets = new Map<string, Rectangle>();
        this.animatedBackgroundOffsets = new Map<string, Rectangle>();
        this.animatedForegroundOffsets = new Map<string, Rectangle>();

        for (const symbolDef of SlotDefinition.symbolDefinitions) {
            const texture = GraphicsManager.getTexture(symbolDef.id);
            const sprite = new PIXI.Sprite(texture);

            this.staticSprites.set(symbolDef.id, sprite);

            // Get position offsets if applicable
            const offsetRect = new Rectangle(0, 0, 1, 1);
            const staticRect = Layers.MatrixContent.getPosition(symbolDef.id);
            if (staticRect) {
                offsetRect.x = staticRect.x - masterRect.x;
                offsetRect.y = staticRect.y - masterRect.y;
                offsetRect.width = staticRect.width / masterRect.width;
                offsetRect.height = staticRect.height / masterRect.height;
            }

            this.staticSpriteOffsets.set(symbolDef.id, offsetRect);

            const animatedBackground = this.generateAnimatedBackground(symbolDef.id);
            const staticBackground = this.generateStaticBackground(symbolDef.id);

            sprite.anchor.set(0.5);
            sprite.visible = false;
            Layers.MatrixContent.add(sprite);

            const animatedForeground = this.generateAnimatedForeground(symbolDef.id);

            this.staticSprite = sprite;
            this.staticBackground = staticBackground;
            this.animatedBackground = animatedBackground;
            this.animatedForeground = animatedForeground;
        }
    }

    private generateStaticBackground(symbolId: string) {
        let staticBackground;
        const masterRect = Layers.MatrixContent.getPosition(this.masterPosition);

        if (GraphicsManager.hasTexture(symbolId + "_bg")) {
            staticBackground = GraphicsManager.createSprite([symbolId + "_bg"]);

            this.staticBackgrounds.set(symbolId, staticBackground);
            staticBackground.visible = false;
            staticBackground.anchor.set(0.5);
            Layers.MatrixContent.add(staticBackground);

            // Get position offsets if applicable
            const bgOffsetRect = new Rectangle(0, 0, 1, 1);
            const bgStaticRect = Layers.MatrixContent.getPosition(symbolId + "_bg");
            if (bgStaticRect) {
                bgOffsetRect.x = bgStaticRect.x - masterRect.x;
                bgOffsetRect.y = bgStaticRect.y - masterRect.y;
                bgOffsetRect.width = bgStaticRect.width / masterRect.width;
                bgOffsetRect.height = bgStaticRect.height / masterRect.height;
            }

            this.staticBackgroundOffsets.set(symbolId, bgOffsetRect);
        }

        return staticBackground;
    }

    private generateAnimatedBackground(symbolId: string) {
        const animatedBackground = GraphicsManager.createAnimation([symbolId + "_back"]);
        const masterRect = Layers.MatrixContent.getPosition(this.masterPosition);

        if (animatedBackground) {
            this.animatedBackgrounds.set(symbolId, animatedBackground);
            animatedBackground.loop = true;
            animatedBackground.stop();
            animatedBackground.visible = false;
            animatedBackground.anchor.set(0.5);
            Layers.MatrixContent.add(animatedBackground);

            // Get position offsets if applicable
            const bgOffsetRect = new Rectangle(0, 0, 1, 1);
            const bgStaticRect = Layers.MatrixContent.getPosition(symbolId + "_back");
            if (bgStaticRect) {
                bgOffsetRect.x = bgStaticRect.x - masterRect.x;
                bgOffsetRect.y = bgStaticRect.y - masterRect.y;
                bgOffsetRect.width = bgStaticRect.width / masterRect.width;
                bgOffsetRect.height = bgStaticRect.height / masterRect.height;
            }

            this.animatedBackgroundOffsets.set(symbolId, bgOffsetRect);
        }

        return animatedBackground;
    }

    private generateAnimatedForeground(symbolId: string) {
        const animatedForeground = GraphicsManager.createAnimation([symbolId + "_fore"]);
        const masterRect = Layers.MatrixContent.getPosition(this.masterPosition);

        if (animatedForeground) {
            this.animatedForegrounds.set(symbolId, animatedForeground);
            animatedForeground.loop = true;
            animatedForeground.stop();
            animatedForeground.visible = false;
            animatedForeground.anchor.set(0.5);
            Layers.MatrixContent.add(animatedForeground);

            // Get position offsets if applicable
            const bgOffsetRect = new Rectangle(0, 0, 1, 1);
            const bgStaticRect = Layers.MatrixContent.getPosition(symbolId + "_fore");
            if (bgStaticRect) {
                bgOffsetRect.x = bgStaticRect.x - masterRect.x;
                bgOffsetRect.y = bgStaticRect.y - masterRect.y;
                bgOffsetRect.width = bgStaticRect.width / masterRect.width;
                bgOffsetRect.height = bgStaticRect.height / masterRect.height;
            }

            this.animatedForegroundOffsets.set(symbolId, bgOffsetRect);
        }

        return animatedForeground;
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/components/matrix/symbol/symbol-component.ts